/*
 * sola-instock-first.js — TRUE collection-wide "in-stock first, sold-out at the bottom".
 *
 * WHY: Shopify can't sort smart collections by availability, and Liquid only sees one
 * page (24) at a time — so global in-stock-first is impossible server-side. Progressive
 * loading also can't do it by *placing* cards as they arrive: a page-5 in-stock item has
 * to appear above a page-1 sold-out item the shopper has already scrolled past.
 *
 * DESIGN — downloading is decoupled from rendering so BOTH in-stock and sold-out paint
 * instantly from memory (no per-scroll network wait):
 *   Downloader: runs a couple pages AHEAD of the scroll, fetching pages via the Section
 *     Rendering API. In-stock cards go into inStockQueue; sold-out (their own
 *     `.sola-card__tag--out` badge) into soldBuffer. Pauses once the in-stock queue is
 *     comfortably ahead; resumes as rendering drains it.
 *   Renderer: on scroll, pulls 24 in-stock from the queue (phase 1). When in-stock are
 *     exhausted and the whole collection is downloaded, reveals sold-out from soldBuffer
 *     24 at a time (phase 2). All from memory => instant.
 * Result: scroll through in-stock only, then sold-out — collection-wide, smooth both ways.
 * Sold-out stay on the page (their own product pages remain indexed regardless).
 *
 * Cards fade in on render. Rendering is driven by an IntersectionObserver AND a throttled
 * scroll/resize backstop (fast scrolling can outrun the observer alone).
 *
 * Runs on BOTH the collection Shop grid and the /search results grid — whichever grid
 * carries [data-instock-first]. The page-fetch section is read from the grid's
 * [data-cards-section] (sola-collection-cards vs sola-search-cards); on /search the
 * search term (`q`) is preserved on every fetch so `search.results` stays populated.
 *
 * Only activates on the DEFAULT browse view (no facet filter / sort). The search term
 * itself is part of the base /search view, so it does NOT disqualify. When the shopper
 * filters or sorts, collection-filters.js takes over with native pagination and this
 * stays dormant. Re-inits after a section swap (MutationObserver on the section root);
 * listeners/observers are torn down when the grid is replaced or the results are done.
 */
(function () {
  var root = document.querySelector('[data-section-id]');
  if (!root) return;
  var SOLD_SELECTOR = '.sola-card__tag--out';
  var CARDS_SECTION = 'sola-collection-cards';  // default grid-only section (collection Shop)
  var BATCH = 24;          // cards rendered per scroll step
  var PREFETCH = 24;       // keep ~1 page of in-stock buffered ahead of the render head
  // (was 72 + eager image pre-warming for every buffered card — on mobile that meant a
  // several-second burst of fetch/parse/decode work after every load & filter swap, which
  // is exactly the window where taps recorded poor INP. The render path pulls from memory
  // either way; images now load when the card is appended, like any lazy grid.)

  // Run background work when the main thread is free so it never competes with a tap.
  function idle(fn) {
    if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 300 });
    else setTimeout(fn, 50);
  }

  function loaderApplies() {
    // In-stock-first runs on the base browse view AND on filtered views — nextUrl() pages
    // through whatever filters are active (see below), so the loader replaces native pagination
    // there too (in-stock first, sold-out at the bottom). It stays OFF only when an explicit,
    // non-default sort is chosen: that chosen order must be preserved, so collection-filters.js
    // owns native pagination for sorted views. (`q` on /search and empty/`manual` params are
    // NOT refinements and don't disable the loader.)
    var explicitSort = false;
    new URLSearchParams(location.search).forEach(function (value, key) {
      if (key === 'sort_by' && value !== '' && value !== 'manual') explicitSort = true;
    });
    return !explicitSort;
  }

  function isSold(card) {
    return !!(card.querySelector && card.querySelector(SOLD_SELECTOR));
  }

  // Dedup key = the product HANDLE, not the raw href. The server renders page-1 cards with
  // clean URLs (/products/x), but the loader's own pages are fetched WITH filter.v.availability
  // applied, so Shopify decorates those same products' URLs with faceted-context tracking params
  // (?_pos=&_fid=&_ss=&variant=). Keying on the full href would treat "/products/x" and
  // "/products/x?_pos=1&_ss=r" as different products, so every in-stock item on page 1 would be
  // re-appended as a duplicate. The handle is stable across both URL forms.
  function keyOf(card) {
    var href = card && card.getAttribute && card.getAttribute('href');
    if (!href) return null;
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : href;
  }

  function enter(card) {                              // fade/slide-in for JS-rendered cards
    if (card.classList) card.classList.add('sola-card--enter');
  }

  function init() {
    var grid = root.querySelector('[data-product-grid]');
    if (!grid || !grid.hasAttribute('data-instock-first')) return;
    if (grid.dataset.instockInit === '1') return;   // already handling this grid node
    if (!loaderApplies()) return;                    // base view + filtered views (not sorted)

    var list = grid.querySelector('.collection-list-5');
    if (!list) return;                               // empty results: nothing to do
    grid.dataset.instockInit = '1';

    // Which grid-only section to page through (collection vs search); default = collection.
    var cardsSection = grid.getAttribute('data-cards-section') || CARDS_SECTION;

    var pager = grid.querySelector('.w-pagination-wrapper');
    if (pager) pager.style.display = 'none';

    // Downloaded-but-not-yet-rendered cards live here.
    var inStockQueue = [];                           // in-stock awaiting render (phase 1)
    var soldBuffer = [];                             // sold-out awaiting render (phase 2)
    var seen = Object.create(null);                  // product hrefs already shown/queued (dedup)
    Array.prototype.slice.call(list.children).forEach(function (c) {
      var k = keyOf(c); if (k) seen[k] = 1;
      if (isSold(c)) { soldBuffer.push(c); list.removeChild(c); }  // strip page-1 sold-out
    });

    var status = document.createElement('div');
    status.className = 'sola-loadmore';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sola-loadmore__btn';
    btn.textContent = 'Load more';
    btn.hidden = true;
    status.appendChild(btn);
    grid.appendChild(status);

    var sentinel = document.createElement('div');
    sentinel.className = 'sola-instock-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    grid.appendChild(sentinel);

    // Fetch AVAILABILITY-FILTERED pages so each request is dense: all in-stock first
    // (dlAvail '1'), then all sold-out ('0'). Avoids scanning the whole collection to surface
    // a small in-stock set (e.g. 73 of 568 hoodies trickling in one page at a time). Page 1 is
    // already on screen, deduped via `seen`, so availability pages start at page 1.
    var urlAvail = new URLSearchParams(location.search).get('filter.v.availability');
    var dl = { page: 1, done: false, busy: false };
    var dlAvail = urlAvail || '1';
    var inStockDone = (urlAvail === '0');            // out-of-stock-only view: skip the in-stock phase
    var renderPhase = 1;                             // 1 = in-stock, 2 = sold-out
    var done = false, io = null, ticking = false;

    function nextUrl() {
      // Preserve the current query (q on /search + active filters); add the availability phase.
      var u = new URL(location.pathname + location.search, location.origin);
      u.searchParams.set('filter.v.availability', dlAvail);
      u.searchParams.set('page', String(dl.page));
      u.searchParams.set('section_id', cardsSection);
      return u.toString();
    }

    function finish() {
      if (done) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (io) io.disconnect();
      status.remove();
      sentinel.remove();
    }

    // --- Downloader: keep the in-stock queue topped up, ahead of the render head. ---
    function pump() {
      if (done || dl.done || dl.busy) return;
      var buf = (dlAvail === '1') ? inStockQueue : soldBuffer;
      if (buf.length >= PREFETCH) return;            // enough of the current phase buffered; pause
      dl.busy = true;
      status.classList.add('is-loading');
      fetch(nextUrl(), { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshList = doc.querySelector('[data-product-grid] .collection-list-5');
          var cards = freshList ? Array.prototype.slice.call(freshList.children) : [];
          if (!cards.length) {
            // in-stock exhausted → switch to the sold-out phase (unless the URL pins availability)
            if (dlAvail === '1' && !urlAvail) { dlAvail = '0'; dl.page = 1; inStockDone = true; }
            else { dl.done = true; inStockDone = true; }
            return;
          }
          cards.forEach(function (c) {
            var k = keyOf(c);
            if (k && seen[k]) return;                        // already on screen / queued
            if (k) seen[k] = 1;
            if (isSold(c)) { soldBuffer.push(c); }
            else { inStockQueue.push(c); }
          });
          dl.page++;
        })
        .catch(function () { dl.done = true; inStockDone = true; })  // network error: stop downloading
        .finally(function () {
          dl.busy = false;
          if (!dl.done && !done) status.classList.remove('is-loading');
          idle(pump);                                 // continue prefetching, but off the critical path
          render();                                   // data may unblock a waiting render
        });
    }

    // --- Renderer: paint from memory whenever the sentinel is near the viewport. ---
    function renderChunk(queue) {
      var n = Math.min(BATCH, queue.length);
      for (var i = 0; i < n; i++) { var c = queue.shift(); enter(c); list.appendChild(c); }
      return n;
    }

    function render() {
      if (done) return;
      if (!document.contains(sentinel)) { finish(); return; }
      var r = sentinel.getBoundingClientRect();
      if (r.top >= window.innerHeight * 2) return;    // not near the bottom yet

      if (renderPhase === 1) {
        if (inStockQueue.length) {
          renderChunk(inStockQueue);
          pump();                                     // refill what we just drained
          requestAnimationFrame(render);              // keep going if still near bottom
        } else if (inStockDone) {
          renderPhase = 2;                            // in-stock exhausted -> sold-out
          requestAnimationFrame(render);
        } else {
          pump();                                     // starved: waiting on the network
        }
      } else {
        if (soldBuffer.length) {
          renderChunk(soldBuffer);
          requestAnimationFrame(render);
        } else {
          finish();
        }
      }
    }

    function onScroll() {                             // throttled backstop for the observer
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; render(); });
    }

    btn.addEventListener('click', render);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (e) {
        if (e[0].isIntersecting) render();
      }, { rootMargin: '800px' });
      io.observe(sentinel);
    } else {
      btn.hidden = false;
    }

    pump();                                           // start prefetching immediately
    render();                                         // fill the first viewport
  }

  // collection-filters.js replaces root's children on every facet/sort/pager swap.
  // Re-run init on those swaps (childList only — our own card inserts are deeper, so
  // they don't retrigger this). init() no-ops on grids it already owns or non-default views.
  // Deferred to idle time so the swap's paint (the part the user is waiting on) lands first;
  // the sold-card strip + prefetch spin-up then start without blocking the interaction.
  new MutationObserver(function () { idle(init); }).observe(root, { childList: true });
  init();
})();
